import ExcelJS from 'exceljs';
import i18n from 'i18next';

export const BULK_SHIPMENT_MAX_ROWS = 50;
export const BULK_SHIPMENT_GUIDE_FILL = 'FFF9C4';

export type BulkShipmentUpdateType = 'ShippingConfirmed' | 'Delivered';

const getColumns = (updateType: BulkShipmentUpdateType): string[] =>
    updateType === 'ShippingConfirmed'
        ? ['shippingListNo', 'desi', 'packageCount', 'description']
        : ['shippingListNo', 'description'];

const getSampleRows = (updateType: BulkShipmentUpdateType): Record<string, string | number>[] => {
    if (updateType === 'ShippingConfirmed') {
        return [
            {
                shippingListNo: '5607201600001',
                desi: 3,
                packageCount: 1,
                description: 'MP toplu sevk',
            },
            {
                shippingListNo: '5606291700001',
                desi: 2,
                packageCount: 1,
                description: 'MP toplu sevk',
            },
        ];
    }

    return [
        { shippingListNo: '5606291100003', description: 'MP toplu teslim' },
        { shippingListNo: '5606261600007', description: 'MP toplu teslim' },
    ];
};

export const downloadBulkShipmentTemplate = async (updateType: BulkShipmentUpdateType) => {
    const columns = getColumns(updateType);
    const samples = getSampleRows(updateType);
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Sayfa-1');

    sheet.columns = columns.map((key) => ({
        header: i18n.t(`excel.${key}`),
        key,
        width: key === 'description' ? 28 : 22,
    }));

    const headerRow = sheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

    // 50 sarı satır: tek seferde en fazla 50 sevkiyat sığacağını gösterir
    for (let i = 0; i < BULK_SHIPMENT_MAX_ROWS; i++) {
        const sample = samples[i];
        const rowValues = columns.map((key) => (sample ? sample[key] ?? '' : ''));
        const row = sheet.addRow(rowValues);
        row.alignment = { vertical: 'middle', horizontal: 'center' };
        for (let col = 1; col <= columns.length; col++) {
            const cell = row.getCell(col);
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: BULK_SHIPMENT_GUIDE_FILL },
            };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' },
            };
        }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const fileName =
        updateType === 'ShippingConfirmed' ? 'Sevk_Edildi_Sablon.xlsx' : 'Teslim_Edildi_Sablon.xlsx';
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
};

const cellToString = (value: ExcelJS.CellValue): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value).trim();
    }
    if (typeof value === 'object' && 'text' in value && typeof (value as { text?: unknown }).text === 'string') {
        return String((value as { text: string }).text).trim();
    }
    if (typeof value === 'object' && 'result' in value) {
        return cellToString((value as { result: ExcelJS.CellValue }).result);
    }
    return String(value).trim();
};

export const countBulkShipmentDataRows = async (file: File): Promise<number> => {
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());
    const worksheet = workbook.worksheets[0];
    if (!worksheet) return 0;

    let count = 0;
    worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;
        const values = Array.isArray(row.values) ? row.values.slice(1) : [];
        const hasData = values.some((v) => cellToString(v as ExcelJS.CellValue) !== '');
        if (hasData) count += 1;
    });

    return count;
};
